import { AI_PACKS, creditCost, FACT_FIELDS, FACT_GROUPS, factField, factOrder, mediaKind, mediaRow, missingTypeFields, packRow, parseFuseTable, parseTimingSheet, sinceOilChange, type FactGroup, type SinceOilChange } from "@szerviz/core";
import { Icon } from "./Icon";
import { useCallback, useEffect, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useApp, useCan, useFeature } from "../context";
import type { AiCredits, TopQuestion, Vehicle, VehicleFact, VehicleTypeNode } from "../data";
import { RewardProgress } from "./AiCreditPanel";
import { AiBadge } from "./AiBadge";
import { ErrorText } from "./ErrorText";
import { Dictate, appendDictation } from "./Dictate";
import { FuseSheet } from "./FuseSheet";
import { Help } from "./Help";
import { TimingSheet } from "./TimingSheet";
import { TypePicker } from "./TypePicker";

/** Shown open: the basic data (oil change, other fluids, maintenance plan); the other groups are folded away. */
const BASIC_GROUPS: readonly FactGroup[] = ["oilChange", "fluids", "service", "asked"];

/**
 * Not offered in the mechanic's own add form: pictures need a link and come from the AI lookups;
 * the fuse sheet is a whole table with its sources, only from the lookup.
 */
const NOT_ADDED_BY_HAND = new Set(["diagram", "fuseTable", "timingSheet"]);

/** Shown as a drawn sheet, not as a table row. */
const DRAWN_FIELDS = new Set(["fuseTable", "timingSheet"]);

/**
 * The oil change of THIS vehicle (its own closed work orders, never shared): the newest
 * closed ones are looked at until one with an oil change line is found.
 */
async function loadSinceOilChange(store: ReturnType<typeof useApp>["store"], vehicle: Vehicle): Promise<SinceOilChange | null> {
  const closed = (await store.workOrdersOfVehicle(vehicle.id))
    .filter((o) => o.status === "closed" && o.closedAt)
    .sort((a, b) => b.closedAt!.localeCompare(a.closedAt!))
    .slice(0, 10);
  for (const o of closed) {
    const d = await store.getWorkOrder(o.id);
    const found = d && sinceOilChange([{ ...o, items: d.items }], vehicle.odometerKm ?? null);
    if (found) return found;
  }
  return null;
}

/**
 * Type data sheet of the own vehicle (shared vehicle knowledge): first the vehicle is put
 * into the shared type tree, then the sheet shows fill quantities, torques, typical
 * faults and tips; mechanics confirm or reject each fact.
 */
export function VehicleDataSheet({ vehicle, onChanged }: { vehicle: Vehicle; onChanged: () => void }) {
  const { t, i18n } = useTranslation();
  const { store } = useApp();
  const [path, setPath] = useState<VehicleTypeNode[]>([]);
  const [facts, setFacts] = useState<VehicleFact[]>([]);
  const [field, setField] = useState("tip");
  const [value, setValue] = useState("");
  const [error, setError] = useState<unknown>(null);
  const fillOn = useFeature("aiDataSheet");
  const [canFill, setCanFill] = useState(false);
  const [canAsk, setCanAsk] = useState(false);
  // "Kérdezd az MI-t" spends the workshop's AI credit: the administrator's and the owner's (2026-11-04)
  const mayAsk = useCan("aiAsk");
  const [filling, setFilling] = useState(false);
  const [sinceOil, setSinceOil] = useState<SinceOilChange | null>(null);
  // "Change type" only opens the picker: the saved type stays until another one is saved
  const [changing, setChanging] = useState(false);

  const load = useCallback(async () => {
    try {
      if (vehicle.typeId) {
        setPath(await store.vehicleTypePath(vehicle.typeId));
        setFacts(await store.vehicleDataSheet(vehicle.id));
        // shown only when known; a failure here never hides the sheet
        setSinceOil(await loadSinceOilChange(store, vehicle).catch(() => null));
        const ai = fillOn && (await store.aiAvailable());
        setCanAsk(ai);
        setCanFill(ai && !(await store.typeFilled(vehicle.id)));
      }
    } catch (err) {
      setError(err);
    }
  }, [store, vehicle, fillOn]);
  useEffect(() => void load(), [load]);

  const run = async (fn: () => Promise<unknown>, changed = false) => {
    setError(null);
    try {
      await fn();
      if (changed) {
        setChanging(false);
        onChanged();
      }
      else await load();
    } catch (err) {
      setError(err);
    }
  };

  if (!vehicle.typeId || changing) {
    const missing = missingTypeFields(vehicle);
    return (
      <section className="data-sheet" aria-label={t("dataSheet.title")}>
        <h2>
          {t("dataSheet.title")} <Help id="dataSheet" />
        </h2>
        <ErrorText error={error} />
        {changing ? (
          <p className="type-path">
            {path.map((n) => n.name).join(" › ")}{" "}
            <button type="button" className="link" onClick={() => setChanging(false)}>
              {t("common.cancel")}
            </button>
          </p>
        ) : (
          <p className="muted">{t("dataSheet.noType")}</p>
        )}
        <TypePicker vehicle={vehicle} onChosen={(id) => void run(() => store.setVehicleType(vehicle.id, id), true)} />
        <p className="muted">{t("dataSheet.noneFits")}</p>
        <div className="actions">
          <button type="button" disabled={missing.length > 0} onClick={() => void run(() => store.setVehicleType(vehicle.id), true)}>
            {t("dataSheet.newType")}
          </button>
          {missing.length > 0 && (
            <span className="missing">{t("dataSheet.missing", { fields: missing.map((k) => t(`vehicle.${k}`)).join(", ") })}</span>
          )}
        </div>
      </section>
    );
  }

  // most important first (FACT_FIELDS order); the basic groups open, the rest folded away
  // the earlier timing rows only while there is no own timing sheet
  const hasTimingSheet = facts.some((f) => f.field === "timingSheet");
  const groups = FACT_GROUPS.filter((g) => g !== "media").map((g) => ({
    g,
    items: facts.filter((f) => factField(f.field)?.group === g && !DRAWN_FIELDS.has(f.field) && !(hasTimingSheet && f.field === "pTiming")).sort((a, b) => factOrder(a.field) - factOrder(b.field)),
  })).filter((x) => x.items.length > 0 || (x.g === "oilChange" && sinceOil));
  const mainGroups = groups.filter((x) => BASIC_GROUPS.includes(x.g));
  const otherGroups = groups.filter((x) => !BASIC_GROUPS.includes(x.g));
  const media = bestMedia(facts.filter((f) => f.field === "diagram"));
  const vehicleName = path.map((n) => n.name).join(" ");
  // placed only down to its model: the AI lookups wait for the engine variant (most specific value)
  const engineLevel = path.at(-1)?.level === "engine";
  const label = (key: string) => t(`fact.${key}`);
  const vote = (f: VehicleFact, agrees: boolean | null) => void run(() => store.voteVehicleFact(vehicle.id, f.id, agrees));
  const sinceText = sinceOil && [
    sinceOil.km !== null ? `${sinceOil.km.toLocaleString(i18n.language)} km` : null,
    sinceOil.days >= 60 ? t("dataSheet.sinceMonths", { months: Math.floor(sinceOil.days / 30.44) }) : t("dataSheet.sinceDays", { days: sinceOil.days }),
  ].filter(Boolean).join(" · ");
  const hasInterval = facts.some((f) => f.field === "serviceInterval");
  const sinceBadge = sinceOil && hasInterval && (
    <span className="badge ok since-badge" title={t("dataSheet.sinceOilFrom", { number: sinceOil.orderNumber, date: sinceOil.closedAt.slice(0, 10) })}>
      {t("dataSheet.sinceBadge", { value: sinceText })}
    </span>
  );
  const sinceRow = sinceOil && !hasInterval && (
    <tr className="fact-computed">
      <th scope="row" className="fact-label"><FactIcon field="sinceOilChange" />{t("dataSheet.sinceOil")}</th>
      <td className="fact-value" colSpan={2}>
        {sinceText}
        <span className="muted small"> {t("dataSheet.sinceOilFrom", { number: sinceOil.orderNumber, date: sinceOil.closedAt.slice(0, 10) })}</span>
      </td>
    </tr>
  );

  return (
    <section className="data-sheet" aria-label={t("dataSheet.title")}>
      <h2>
        {t("dataSheet.title")} <Help id="dataSheet" />
      </h2>
      <p className="type-path">
        {path.map((n) => n.name).join(" › ")}{" "}
        <button type="button" className="link" onClick={() => setChanging(true)}>
          {t("dataSheet.changeType")}
        </button>
      </p>
      <ErrorText error={error} />
      {groups.length === 0 && media.length === 0 && !facts.some((f) => DRAWN_FIELDS.has(f.field)) && <p className="muted">{t("dataSheet.empty")}</p>}
      {path.length > 0 && !engineLevel && (
        <details className="type-engine" open>
          <summary>{t("typePicker.pickEngine")}</summary>
          <p className="muted">{t("typePicker.modelLevel")}</p>
          <TypePicker vehicle={vehicle} start={path} onChosen={(id) => void run(() => store.setVehicleType(vehicle.id, id), true)} />
        </details>
      )}
      {canFill && engineLevel && (
        <div className="actions">
          <button
            type="button"
            disabled={filling}
            title={t("dataSheet.aiFillNote")}
            onClick={() => {
              setFilling(true);
              void run(() => store.fillDataSheet(vehicle.id)).finally(() => setFilling(false));
            }}
          >
            {filling ? t("dataSheet.aiFilling") : t("dataSheet.aiFill")}
          </button>
        </div>
      )}
      {mainGroups.length > 0 && <FactTable groups={mainGroups} label={label} vote={vote} extra={{ oilChange: sinceRow }} badges={{ serviceInterval: sinceBadge }} />}
      {media.length > 0 && <MediaCards items={media} vote={vote} showPack />}
      {(otherGroups.length > 0 || facts.some((f) => DRAWN_FIELDS.has(f.field))) && (
        <details className="facts-more">
          <summary>{t("dataSheet.more")}</summary>
          {otherGroups.length > 0 && <FactTable groups={otherGroups} label={label} vote={vote} />}
          <TimingSheets facts={facts} vehicleName={vehicleName} vote={vote} />
          <FuseSheets facts={facts} vehicleName={vehicleName} vote={vote} />
        </details>
      )}
      {canAsk && mayAsk && engineLevel && <AskAi vehicleId={vehicle.id} vehicleName={vehicleName} facts={facts} onAnswered={load} vote={vote} />}
      <details className="fact-add">
        <summary>{t("dataSheet.add")}</summary>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void run(async () => {
              await store.addVehicleFact(vehicle.id, field, value);
              setValue("");
            });
          }}
        >
          <label className="field">
            <span>{t("dataSheet.field")}</span>
            <select id="fact-field" value={field} onChange={(e) => setField(e.target.value)}>
              {FACT_FIELDS.filter((f) => !NOT_ADDED_BY_HAND.has(f.key)).map((f) => (
                <option key={f.key} value={f.key}>{label(f.key)}</option>
              ))}
            </select>
          </label>
          <label className="field wide">
            <span>{t("dataSheet.value")}</span>
            <textarea id="fact-value" rows={2} value={value} onChange={(e) => setValue(e.target.value)} />
            <Dictate label={t("dataSheet.value")} onText={(x) => setValue((v) => appendDictation(v, x))} />
          </label>
          <div className="actions">
            <button type="submit" className="primary" disabled={!value.trim()}>{t("dataSheet.addButton")}</button>
            {!value.trim() && <span className="missing">{t("dataSheet.valueMissing")}</span>}
          </div>
        </form>
      </details>
    </section>
  );
}

function FactTable({ groups, label, vote, extra, badges }: {
  groups: { g: FactGroup; items: VehicleFact[] }[];
  label: (key: string) => string;
  vote: (f: VehicleFact, agrees: boolean | null) => void;
  /** Computed rows at the end of a group (e.g. since the last oil change). */
  extra?: Partial<Record<FactGroup, ReactNode>>;
  /** Extra badges under a field's value (e.g. since the last oil change under the interval). */
  badges?: Record<string, ReactNode>;
}) {
  const { t } = useTranslation();
  // a maintenance plan item "Air filter: 30 000 km / 2 years" reads as a row: what | when
  const split = (f: VehicleFact) => (f.field === "maintenance" || f.field === "custom" ? f.value.match(/^([^:]{2,60}):\s*(.+)$/s) : null);
  const pr = (f: VehicleFact) => (f.field.startsWith("p") ? packRow(f.value) : null);
  const rowLabel = (f: VehicleFact) => { const p = pr(f); return p ? t(`packRow.${p[0]}`) : split(f)?.[1] ?? label(f.field); };
  const rowValue = (f: VehicleFact) => pr(f)?.[1] ?? split(f)?.[2] ?? f.value;
  return (
        <table className="facts-table">
          {groups.map(({ g, items }) => (
            <tbody key={g} className={`fact-group-${g}`}>
              <tr className="fact-group-head">
                <th colSpan={3} scope="colgroup">{t(`factGroup.${g}`)}</th>
              </tr>
              {items.map((f) => (
                <tr key={f.id} className={f.confirmed ? "confirmed" : f.origin === "ai" ? "unchecked" : undefined}>
                  <th scope="row" className="fact-label"><FactIcon field={f.field} />{rowLabel(f)}</th>
                  <td className="fact-value">
                    {rowValue(f)}
                    {f.sourceUrl && (
                      <>
                        {" "}
                        <a className="fact-source" href={f.sourceUrl} target="_blank" rel="noopener noreferrer" aria-label={t("dataSheet.source")} title={f.sourceUrl}>↗</a>
                      </>
                    )}
                    <span className="fact-status">
                      <FactBadge f={f} />
                      {badges?.[f.field]}
                    </span>
                  </td>
                  <td className="vote">
                    <VoteButtons f={f} vote={vote} />
                  </td>
                </tr>
              ))}
              {extra?.[g]}
            </tbody>
          ))}
        </table>
  );
}

/** The fuse sheet(s) of the type, drawn by Garagevo, with the votes on the whole table. */
function FuseSheets({ facts, vehicleName, vote }: { facts: VehicleFact[]; vehicleName: string; vote: (f: VehicleFact, agrees: boolean | null) => void }) {
  return (
    <>
      {facts.filter((f) => f.field === "fuseTable").map((f) => {
        const table = parseFuseTable(f.value);
        return table ? (
          <FuseSheet key={f.id} table={table} vehicle={vehicleName} unchecked={!f.confirmed && f.origin === "ai"}
            status={<><FactBadge f={f} /><span className="vote"><VoteButtons f={f} vote={vote} /></span></>} />
        ) : null;
      })}
    </>
  );
}

/** The own timing sheet of the type, with its diagrams as links and the votes on the whole sheet. */
function TimingSheets({ facts, vehicleName, vote }: { facts: VehicleFact[]; vehicleName: string; vote: (f: VehicleFact, agrees: boolean | null) => void }) {
  const diagrams = bestMedia(facts.filter((f) => f.field === "diagram" && mediaRow(f.value).pack === "timing" && f.sourceUrl))
    .map((f) => ({ title: mediaRow(f.value).title, url: f.sourceUrl! }));
  return (
    <>
      {facts.filter((f) => f.field === "timingSheet").map((f) => {
        const sheet = parseTimingSheet(f.value);
        return sheet ? (
          <TimingSheet key={f.id} sheet={sheet} vehicle={vehicleName} unchecked={!f.confirmed && f.origin === "ai"} diagrams={diagrams}
            status={<><FactBadge f={f} /><span className="vote"><VoteButtons f={f} vote={vote} /></span></>} />
        ) : null;
      })}
    </>
  );
}

/** The state of a fact: confirmed with its share, "check it" for an AI one, or the votes. */
function FactBadge({ f }: { f: VehicleFact }) {
  const { t } = useTranslation();
  return f.confirmed ? (
    <span className="badge ok" title={t("dataSheet.confirmed", { percent: f.percent ?? 100, count: f.confirms })}>
      {t("dataSheet.confirmedShort", { percent: f.percent ?? 100 })}
    </span>
  ) : f.origin === "ai" ? (
    <span className="badge warn" title={t("dataSheet.votes", { confirms: f.confirms, rejects: f.rejects })}>
      <AiBadge short /> {t("dataSheet.check")}
    </span>
  ) : (
    <span className="badge" title={t("dataSheet.votes", { confirms: f.confirms, rejects: f.rejects })}>
      {t("dataSheet.votesShort", { confirms: f.confirms, rejects: f.rejects })}
    </span>
  );
}

function VoteButtons({ f, vote }: { f: VehicleFact; vote: (f: VehicleFact, agrees: boolean | null) => void }) {
  const { t } = useTranslation();
  return (
    <>
      <button type="button" aria-pressed={f.myVote === true} aria-label={t("dataSheet.agree")} title={t("dataSheet.agree")}
        className={`icon-vote${f.myVote === true ? " selected" : ""}`}
        onClick={() => vote(f, f.myVote === true ? null : true)}>
        ✓
      </button>
      <button type="button" aria-pressed={f.myVote === false} aria-label={t("dataSheet.disagree")} title={t("dataSheet.disagree")}
        className={`icon-vote${f.myVote === false ? " selected danger" : ""}`}
        onClick={() => vote(f, f.myVote === false ? null : false)}>
        ✗
      </button>
    </>
  );
}

/**
 * Pictures and documents as cards: a preview loaded from the source (no referrer, lazy, with
 * a fallback icon), the title, the site and "Open" in a new tab. Garagevo keeps only the link.
 */
function MediaCards({ items, vote, showPack }: { items: VehicleFact[]; vote: (f: VehicleFact, agrees: boolean | null) => void; showPack?: boolean }) {
  const { t } = useTranslation();
  return (
    <section className="media-block" aria-label={t("factGroup.media")}>
      <h3 className="media-head">{t("factGroup.media")} <Help id="dataSheetMedia" /></h3>
      <ul className="media-cards">
        {items.map((f) => {
          const { pack, title } = mediaRow(f.value);
          const url = f.sourceUrl ?? "";
          let domain = "";
          try {
            domain = new URL(url).hostname.replace(/^www\./, "");
          } catch {
            domain = "";
          }
          return (
            <li key={f.id} className={`media-card${f.confirmed ? " confirmed" : ""}`}>
              <MediaPreview kind={url ? mediaKind(url) : "page"} src={f.previewUrl ?? null} />
              <div className="media-body">
                {showPack && pack && <small className="media-pack">{t(`pack.${pack}`)}</small>}
                <strong className="media-title">{title}</strong>
                <small className="muted">{domain}</small>
                <div className="media-actions">
                  {url && (
                    <a className="button media-open" href={url} target="_blank" rel="noopener noreferrer" referrerPolicy="no-referrer">
                      {t("dataSheet.mediaOpen")}
                    </a>
                  )}
                  <FactBadge f={f} />
                  <span className="vote"><VoteButtons f={f} vote={vote} /></span>
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function MediaPreview({ kind, src }: { kind: "image" | "pdf" | "page"; src: string | null }) {
  const [failed, setFailed] = useState(false);
  if (src && !failed && kind !== "pdf") {
    return <img className="media-thumb" src={src} alt="" loading="lazy" referrerPolicy="no-referrer" onError={() => setFailed(true)} />;
  }
  return (
    <span className={`media-thumb media-fallback media-${kind}`} aria-hidden="true">
      <Icon name={kind === "image" ? "photo" : "workOrder"} size={36} />
    </span>
  );
}

/** One small emoji per field at the start of its label (decorative; the owner's approved look). */
const FIELD_EMOJI: Record<string, string> = {
  oilCapacity: "🛢️", oilSpec: "🏷️", oilFilter: "🧰", oilDrainPlug: "🔩", serviceInterval: "🕒", sinceOilChange: "🕒",
  gearboxManual: "⚙️", gearboxAuto: "⚙️", gearboxOil: "⚙️", transferDiff: "⚙️", brakeFluid: "🛑", coolant: "❄️",
  powerSteering: "🚘", adblue: "💧", maintenance: "📋", timing: "⛓️", custom: "❓", pTiming: "⛓️", fuseTable: "🔌",
  pTorques: "🔩", pFuses: "🔌", pTyres: "🛞", pAircon: "🌬️", torqueWheel: "🔩", torqueDrain: "🔩",
  torqueSpark: "🔩", torqueOther: "🔩", commonFault: "⚠️", tip: "💡", fuseBox: "🔌", tyres: "🛞", diagram: "🖼️",
};

function FactIcon({ field }: { field: string }) {
  return <span className="fact-icon" aria-hidden="true">{FIELD_EMOJI[field] ?? "💧"}</span>;
}


/**
 * The mechanic asks the AI about the vehicle (typed or dictated), e.g. "crankshaft bolt
 * torque": a short answer and the data as table rows, which stay on the data sheet for
 * everyone. The questions of this visit are listed, newest first.
 */
function AskAi({ vehicleId, vehicleName, facts, onAnswered, vote }: { vehicleId: string; vehicleName: string; facts: VehicleFact[]; onAnswered: () => Promise<void>; vote: (f: VehicleFact, agrees: boolean | null) => void }) {
  const { t, i18n } = useTranslation();
  const { store, workshop } = useApp();
  const [question, setQuestion] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [log, setLog] = useState<{ q: string; answer: string; facts: { value: string; source: string | null }[]; cached: boolean; credits: number }[]>([]);
  // quick lookup packs: the mechanic taps a pack, the program sends a fixed query (no text typed in)
  const [packKey, setPackKey] = useState<string | null>(null);
  const [packDone, setPackDone] = useState<{ key: string; credits: number; found: number; known: boolean; cached: boolean } | null>(null);
  const pack = AI_PACKS.find((p) => p.key === packKey) ?? null;
  const factsOf = (field: string) => facts.filter((f) => f.field === field);
  const runPack = async () => {
    if (!pack) return;
    setError(null);
    setBusy(true);
    try {
      const r = await store.aiPack(vehicleId, pack.key);
      setPackDone({ key: pack.key, credits: r.credits, found: r.facts.length, known: !!r.knownSource, cached: r.cached });
      await onAnswered();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };
  const ask = async (ready?: { item: string; label: string }) => {
    const q = ready ? ready.label : question.trim();
    if (!q) return;
    setError(null);
    setBusy(true);
    try {
      const r = await store.askVehicleData(vehicleId, ready ? "" : q, ready?.item);
      setLog((l) => [{ q, ...r }, ...l]);
      if (!ready) setQuestion("");
      setAsking(null);
      await onAnswered();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };
  // credits: the cost before asking, and the reward progress (refreshed after every vote or answer)
  const [credits, setCredits] = useState<AiCredits | null>(null);
  const [top, setTop] = useState<TopQuestion[]>([]);
  // a ready question chosen from the five buttons, waiting for "Kérdezem" (shows its cost first)
  const [asking, setAsking] = useState<{ item: string; label: string; have: boolean } | null>(null);
  const votes = facts.reduce((a, f) => a + (f.myVote === null || f.myVote === undefined ? 0 : 1), 0);
  useEffect(() => {
    let live = true;
    store.aiCredits(workshop.id).then((c) => live && setCredits(c), () => undefined);
    return () => { live = false; };
  }, [store, workshop.id, votes, facts.length, log.length]);
  useEffect(() => {
    let live = true;
    store.aiTopQuestions(vehicleId).then((x) => live && setTop(x), () => undefined);
    return () => { live = false; };
  }, [store, vehicleId, facts.length, log.length]);
  const cost = (kind: "pack" | "question", key?: string) => (credits ? creditCost(credits.costs, kind, key) : null);
  // packs whose lookup starts from known sources (the fuse sheet): cheaper, the tile says so
  const [knownPacks, setKnownPacks] = useState<ReadonlySet<string>>(new Set());
  const sheetPacks = AI_PACKS.filter((p) => p.sheet && facts.every((f) => f.field !== p.field)).map((p) => p.key).join(",");
  useEffect(() => {
    let live = true;
    const keys = sheetPacks ? sheetPacks.split(",") : [];
    Promise.all(keys.map((k) => store.packKnownSource(vehicleId, k).then((on) => (on ? k : null), () => null)))
      .then((on) => live && setKnownPacks(new Set(on.filter((k): k is string => !!k))), () => undefined);
    return () => { live = false; };
  }, [store, vehicleId, sheetPacks, facts.length]);
  /** "7 kredit" in a pale green circle: the lookup is not free, it costs this many credits. */
  const costTag = (n: number | null) =>
    n ? <span className="price-hint" title={t("ask.costTitle")}>{t("ask.costTag", { count: n })}</span> : null;
  /** The text of one of the five buttons. */
  const topLabel = (item: string) => {
    if (item.startsWith("pack:")) return t(`pack.${item.slice(5)}`);
    if (item.startsWith("ask:")) return t(`topQuestion.${item.slice(4)}`);
    const q = item.slice(2);
    return q.charAt(0).toLocaleUpperCase(i18n.language) + q.slice(1);
  };
  const chooseTop = (x: TopQuestion) => {
    setError(null);
    if (x.item.startsWith("pack:")) {
      setAsking(null);
      setPackKey(x.item.slice(5));
      setPackDone(null);
      return;
    }
    setPackKey(null);
    const label = topLabel(x.item);
    // an own question of other workshops is asked as written; a ready question by its key
    if (x.item.startsWith("q:")) {
      setQuestion(label);
      setAsking(null);
    } else setAsking({ item: x.item, label, have: x.have });
  };
  const packRows = pack ? factsOf(pack.field) : [];
  const packSheet = pack?.sheetField ? factsOf(pack.sheetField) : [];
  const packMedia = pack ? bestMedia(facts.filter((f) => f.field === "diagram" && mediaRow(f.value).pack === pack.key)) : [];
  return (
    <div className="ask-ai">
      <h3>{t("ask.title")} <Help id="askAi" /></h3>
      {top.length > 0 && (
        <div className="top-questions" role="group" aria-label={t("ask.top")}>
          <h4>{t("ask.top")}</h4>
          <div className="top-buttons">
            {top.map((x) => {
              const kind = x.item.startsWith("pack:") ? "pack" : "question";
              return (
                <button key={x.item} type="button" className={`top-question${x.have ? " have" : ""}`}
                  aria-pressed={asking?.item === x.item || packKey === x.item.slice(5)}
                  onClick={() => chooseTop(x)}>
                  <span>{topLabel(x.item)}</span>
                  {x.have ? <small className="top-free">{t("ask.free")}</small> : costTag(cost(kind, x.item.slice(5)))}
                </button>
              );
            })}
          </div>
          {asking && (
            <div className="ask-ready">
              <p className="ask-cost-notice">{asking.have ? t("ask.freeNotice") : t("ask.costNotice", { count: cost("question") ?? 0 })}</p>
              <div className="actions">
                <button type="button" className="primary" disabled={busy} onClick={() => void ask(asking)}>{busy ? t("ask.asking") : t("ask.button")}</button>
                <button type="button" onClick={() => setAsking(null)}>{t("common.cancel")}</button>
              </div>
            </div>
          )}
        </div>
      )}
      <div className="pack-tiles" role="group" aria-label={t("ask.packs")}>
        {AI_PACKS.map((p) => {
          const have = factsOf(p.sheetField ?? p.field).length > 0;
          const known = !have && knownPacks.has(p.key);
          return (
            <button key={p.key} type="button" aria-pressed={packKey === p.key}
              className={`pack-tile${packKey === p.key ? " selected" : ""}${have ? " have" : ""}`}
              onClick={() => { setPackKey(packKey === p.key ? null : p.key); setPackDone(null); }}>
              {!have && costTag(cost("pack", p.key))}
              <strong>{t(`pack.${p.key}`)}</strong>
              <small>{have ? t("ask.packHave") : p.sheet ? t("ask.packSheet") : t("ask.packRows", { count: p.rows.length })}</small>
              {known && <small className="pack-known" title={t("ask.packKnownTitle")}>{t("ask.packKnown")}</small>}
            </button>
          );
        })}
      </div>
      {pack && (
        <div className="pack-panel">
          <h4>{t("ask.packWhat", { pack: t(`pack.${pack.key}`) })}</h4>
          {pack.sheet ? (
            packRows.length > 0 ? (
              <FuseSheets facts={packRows} vehicleName={vehicleName} vote={vote} />
            ) : (
              <p className="muted">{packDone?.key === pack.key ? t("ask.packNotFound") : t("ask.packSheetNote")}</p>
            )
          ) : packSheet.length > 0 ? (
            <TimingSheets facts={facts} vehicleName={vehicleName} vote={vote} />
          ) : (
            <table className="facts-table">
              <tbody>
                {pack.rows.map((row) => {
                  const f = packRows.find((x) => packRow(x.value)?.[0] === row);
                  return (
                    <tr key={row}>
                      <th scope="row" className="fact-label">{t(`packRow.${row}`)}</th>
                      <td className={f ? "fact-value" : "fact-value pack-empty"}>
                        {f ? packRow(f.value)![1] : packDone?.key === pack.key ? t("ask.packNotFound") : "?"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
          {(pack.sheetField ? packSheet.length === 0 : packRows.length === 0) && (
            <div className="actions">
              <button type="button" className="primary" disabled={busy} onClick={() => void runPack()}>
                {busy ? t("ask.asking") : t("ask.packRun")}
              </button>
              {cost("pack", pack.key) !== null && <span className="ask-cost-notice">{t("ask.costNotice", { count: cost("pack", pack.key)! })}</span>}
            </div>
          )}
          {packMedia.length > 0 && <MediaCards items={packMedia} vote={vote} />}
          {packDone?.key === pack.key && !packDone.cached && <p className="muted small">{packDone.found > 0 ? t("ask.charged", { count: packDone.credits }) : packDone.credits > 0 ? t("ask.notRefunded", { count: packDone.credits }) : t("ask.refunded")}</p>}
          {packDone?.key === pack.key && packDone.known && <p className="muted small">{t("ask.packKnownUsed")}</p>}
          <p className="muted small">{t("ask.packNote")}</p>
        </div>
      )}
      <ErrorText error={error} />
      <details className="ask-own" open={question.length > 0 || undefined}>
        <summary>{t("ask.own")}</summary>
        <label className="field wide">
          <span>{t("ask.label")}</span>
          <textarea id="ask-question" rows={2} value={question} maxLength={500} onChange={(e) => setQuestion(e.target.value)} />
          <Dictate label={t("ask.label")} onText={(s) => setQuestion((v) => appendDictation(v, s))} />
        </label>
        <div className="actions">
          <button type="button" className="primary" disabled={busy || question.trim().length < 3} onClick={() => void ask()}>
            {busy ? t("ask.asking") : t("ask.button")}
          </button>
          {cost("question") !== null && <span className="ask-cost-notice">{t("ask.costNotice", { count: cost("question")! })}</span>}
          <span className="muted small">{t("ask.note")}</span>
        </div>
      </details>
      {log.map((x, i) => (
        <div key={i} className="ask-answer" role="status">
          <p className="ask-q">{x.q}</p>
          {x.answer && <p>{x.answer}</p>}
          <p className="muted small">{x.cached ? t("ask.cachedAnswer") : x.facts.length > 0 ? t("ask.charged", { count: x.credits }) : x.credits > 0 ? t("ask.notRefunded", { count: x.credits }) : t("ask.refunded")}</p>
          {x.facts.length > 0 && (
            <table className="facts-table">
              <tbody>
                {x.facts.map((f, j) => {
                  const m = f.value.match(/^([^:]{2,80}):\s*(.+)$/s);
                  return (
                    <tr key={j}>
                      <th scope="row" className="fact-label">{m?.[1] ?? ""}</th>
                      <td className="fact-value">
                        {m?.[2] ?? f.value}
                        {f.source && (
                          <>
                            {" "}
                            <a className="fact-source" href={f.source} target="_blank" rel="noopener noreferrer" aria-label={t("dataSheet.source")} title={f.source}>↗</a>
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      ))}
      {/* "Aki ad, kap": votes and tips on the shared sheet earn credits */}
      {credits && <RewardProgress c={credits} />}
    </div>
  );
}

/**
 * At most two pictures or documents per lookup (and two general ones), the best first: confirmed,
 * then more confirmations than rejections. The owner (2026-11-01): too much information confuses.
 */
export function bestMedia(items: VehicleFact[], perGroup = 2): VehicleFact[] {
  const score = (f: VehicleFact) => (f.confirmed ? 1000 : 0) + f.confirms - f.rejects;
  const sorted = items.map((f, i) => ({ f, i })).sort((a, b) => score(b.f) - score(a.f) || a.i - b.i).map((x) => x.f);
  const count = new Map<string, number>();
  return sorted.filter((f) => {
    const g = mediaRow(f.value).pack ?? "";
    const n = count.get(g) ?? 0;
    count.set(g, n + 1);
    return n < perGroup;
  });
}
